/**
 * Validates email addresses with proper handling of subdomains and edge cases
 * 
 * This regex validates:
 * - Local part: alphanumeric, dots, hyphens, underscores, plus signs
 * - Domain: alphanumeric, hyphens, dots (for subdomains)
 * - TLD: 2-63 characters, alphanumeric and hyphens allowed
 * - Prevents consecutive dots, leading/trailing dots
 * - Supports multiple subdomains (e.g., user@mail.example.com)
 * - Supports internationalized domain names (IDNs) with xn-- prefixes
 */

// RFC 5322 compliant email regex with improvements for modern use cases
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// TLD validation regex: alphanumeric with optional hyphens (not at start/end)
const TLD_REGEX = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

export function isValidEmail(email: string): boolean {
  // Additional checks for edge cases
  if (!EMAIL_REGEX.test(email)) {
    return false;
  }
  
  // Check for consecutive dots (not handled by regex)
  if (email.includes('..')) {
    return false;
  }
  
  // Check for leading or trailing dots in local part
  const [localPart] = email.split('@');
  if (!localPart || localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }
  
  // Check domain structure
  const domain = email.split('@')[1];
  if (!domain) {
    return false;
  }
  
  // Check for leading or trailing dots in domain
  if (domain.startsWith('.') || domain.endsWith('.')) {
    return false;
  }
  
  // Ensure domain contains at least one dot (enforcing domain.tld structure)
  if (!domain.includes('.')) {
    return false;
  }
  
  // Check TLD length (1-63 characters per RFC 1035)
  const tld = domain.split('.').pop();
  if (!tld || tld.length < 1 || tld.length > 63) {
    return false;
  }
  
  // Ensure TLD is valid: contains at least one letter, may contain hyphens (not at the start or end of a label),
  // and follows RFC standards for TLD naming.
  if (!TLD_REGEX.test(tld) || !/[a-zA-Z]/.test(tld)) {
    return false;
  }
  
  return true;
} 